/**
 * WhatsApp configuration (#1474).
 *
 * Writes phone / WABA / token through staff-api so the token is encrypted
 * with the same ENCRYPTION_KEY the send path already uses. The token field
 * is write-only — the API never returns it. Every write requires
 * `settings.manage_whatsapp`, the same permission the staff-api routes use.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import SyncIcon from '@mui/icons-material/Sync';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import {
  useSaveWhatsAppConfig,
  useSyncWhatsAppTemplates,
  useTestWhatsAppConfig,
  useWhatsAppAdminTemplates,
  useWhatsAppConfig,
  type WhatsAppAdminTemplate,
} from '@/hooks/useWhatsAppSettings';
import { useAuthStore } from '@/stores/authStore';
import { MANAGE_WHATSAPP_PERMISSION } from '../permissions';
import { readWhatsAppTemplateApiError } from './templateValidation';
import { WhatsAppTemplateCreateDialog } from './WhatsAppTemplateCreateDialog';
import {
  WhatsAppTemplateSourcesDialog,
  type WhatsAppTemplateSourceMapping,
} from './WhatsAppTemplateSourcesDialog';
import { formatDateTime } from '@/utils/locale';
import {
  useDeleteWhatsAppTemplate,
  visibleMetaErrorText,
} from './useWhatsAppTemplateMutations';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'error',
  draft: 'default',
};

export function WhatsAppSettingsTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Config, test, sync, and template writes all require settings.manage_whatsapp.
  const canManageWhatsApp = hasPermission(MANAGE_WHATSAPP_PERMISSION);

  const configQuery = useWhatsAppConfig(true);
  const templatesQuery = useWhatsAppAdminTemplates(true);
  const saveConfig = useSaveWhatsAppConfig();
  const testConfig = useTestWhatsAppConfig();
  const syncTemplates = useSyncWhatsAppTemplates();
  const deleteTemplate = useDeleteWhatsAppTemplate();

  const [createOpen, setCreateOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<WhatsAppAdminTemplate | null>(null);
  const [mappingTemplate, setMappingTemplate] = useState<WhatsAppTemplateSourceMapping | null>(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [webhookVerifyToken, setWebhookVerifyToken] = useState('');
  const [statusActive, setStatusActive] = useState(false);
  const [messagingEnabled, setMessagingEnabled] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    const cfg = configQuery.data;
    if (!cfg) return;
    setPhoneNumber(cfg.phoneNumber ?? '');
    setPhoneNumberId(cfg.phoneNumberId ?? '');
    setWabaId(cfg.wabaId ?? '');
    setStatusActive(cfg.status === 'active');
    setMessagingEnabled(cfg.messagingEnabled);
    setAccessToken('');
    setWebhookVerifyToken('');
  }, [configQuery.data]);

  const handleSave = async () => {
    if (!hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
    try {
      await saveConfig.mutateAsync({
        phoneNumber,
        phoneNumberId,
        wabaId,
        status: statusActive ? 'active' : 'pending',
        messagingEnabled,
        ...(accessToken.trim() ? { accessToken: accessToken.trim() } : {}),
        ...(webhookVerifyToken.trim() ? { webhookVerifyToken: webhookVerifyToken.trim() } : {}),
      });
      setAccessToken('');
      setWebhookVerifyToken('');
      notify.success(t('whatsapp.saved'), t('whatsapp.savedDesc'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('whatsapp.saveFailed') }));
    }
  };

  const handleTest = async () => {
    if (!hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
    setTestResult(null);
    try {
      const result = await testConfig.mutateAsync(testTo);
      if (result.success) {
        setTestResult({ success: true, message: t('whatsapp.testOk') });
      } else {
        setTestResult({ success: false, message: result.error ?? t('whatsapp.testFailed') });
      }
    } catch {
      setTestResult({ success: false, message: t('whatsapp.testFailed') });
    }
  };

  const handleSync = async () => {
    if (!hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
    try {
      const result = await syncTemplates.mutateAsync();
      if (result.success) {
        notify.success(t('whatsapp.syncOk'), t('whatsapp.syncOkDesc', { count: result.synced ?? 0 }));
      } else {
        notify.error(t('whatsapp.syncFailed'), result.error ?? t('whatsapp.syncFailed'));
      }
    } catch {
      notify.error(tc('error'), t('whatsapp.syncFailed'));
    }
  };

  const handleDelete = async () => {
    if (!deletingTemplate || !hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
    try {
      await deleteTemplate.mutateAsync({
        name: deletingTemplate.name,
        language: deletingTemplate.language,
      });
      notify.success(t('whatsapp.templateDeleted'), t('whatsapp.templateDeletedDesc'));
      setDeletingTemplate(null);
    } catch (err) {
      const parsed = readWhatsAppTemplateApiError(err);
      if (parsed?.status === 502) {
        notify.error(
          tc('error'),
          visibleMetaErrorText(parsed.metaError) || t('whatsapp.metaErrorUnavailable'),
        );
        return;
      }
      if (parsed?.error === 'template_not_found') {
        notify.error(tc('error'), t('whatsapp.templateNotFound'));
        return;
      }
      notify.error(tc('error'), t('whatsapp.templateDeleteFailed'));
    }
  };

  if (configQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (configQuery.isError) {
    return <Alert severity="error">{t('whatsapp.loadFailed')}</Alert>;
  }

  const cfg = configQuery.data;
  const templates = (templatesQuery.data ?? []) as WhatsAppTemplateSourceMapping[];

  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('whatsapp.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('whatsapp.description')}
        </Typography>
      </Box>

      {cfg?.lastInboundAt && (
        <Typography variant="caption" color="text.secondary">
          {t('whatsapp.lastInbound', { when: formatDateTime(cfg.lastInboundAt) })}
        </Typography>
      )}

      <Stack spacing={2} maxWidth={560}>
        <FormControlLabel
          control={
            <Switch
              checked={messagingEnabled}
              onChange={(e) => setMessagingEnabled(e.target.checked)}
              disabled={!canManageWhatsApp}
            />
          }
          label={t('whatsapp.messagingEnabled')}
        />
        <FormControlLabel
          control={
            <Switch
              checked={statusActive}
              onChange={(e) => setStatusActive(e.target.checked)}
              disabled={!canManageWhatsApp}
            />
          }
          label={t('whatsapp.statusActive')}
        />
        <TextField
          label={t('whatsapp.phoneNumber')}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          helperText={t('whatsapp.phoneNumberHint')}
          autoComplete="off"
          disabled={!canManageWhatsApp}
        />
        <TextField
          label={t('whatsapp.phoneNumberId')}
          value={phoneNumberId}
          onChange={(e) => setPhoneNumberId(e.target.value)}
          autoComplete="off"
          disabled={!canManageWhatsApp}
        />
        <TextField
          label={t('whatsapp.wabaId')}
          value={wabaId}
          onChange={(e) => setWabaId(e.target.value)}
          autoComplete="off"
          disabled={!canManageWhatsApp}
        />
        <TextField
          label={t('whatsapp.accessToken')}
          type="password"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          helperText={
            cfg?.tokenConfigured ? t('whatsapp.accessTokenSetHint') : t('whatsapp.accessTokenHint')
          }
          autoComplete="new-password"
          disabled={!canManageWhatsApp}
        />
        <TextField
          label={t('whatsapp.webhookVerifyToken')}
          type="password"
          value={webhookVerifyToken}
          onChange={(e) => setWebhookVerifyToken(e.target.value)}
          helperText={
            cfg?.webhookSecretConfigured
              ? t('whatsapp.webhookSetHint')
              : t('whatsapp.webhookHint')
          }
          autoComplete="new-password"
          disabled={!canManageWhatsApp}
        />
        {canManageWhatsApp && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => void handleSave()}
              disabled={saveConfig.isPending}
            >
              {saveConfig.isPending ? t('saving') : t('saveChanges')}
            </Button>
          </Box>
        )}
      </Stack>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {t('whatsapp.testTitle')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            label={t('whatsapp.testTo')}
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            sx={{ minWidth: 240 }}
            disabled={!canManageWhatsApp}
          />
          {canManageWhatsApp && (
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => void handleTest()}
              disabled={testConfig.isPending || !testTo.trim()}
            >
              {t('whatsapp.sendTest')}
            </Button>
          )}
        </Stack>
        {testResult && (
          <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
            {testResult.message}
          </Alert>
        )}
      </Box>

      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle1">{t('whatsapp.templatesTitle')}</Typography>
          {canManageWhatsApp && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (!hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
                  setCreateOpen(true);
                }}
              >
                {t('whatsapp.newTemplate')}
              </Button>
              <Button
                size="small"
                startIcon={<SyncIcon />}
                onClick={() => void handleSync()}
                disabled={syncTemplates.isPending}
              >
                {t('whatsapp.sync')}
              </Button>
            </Stack>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('whatsapp.templatesHint')}
        </Typography>
        {templatesQuery.isLoading ? (
          <CircularProgress size={24} />
        ) : templates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('whatsapp.noTemplates')}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('whatsapp.templateName')}</TableCell>
                <TableCell>{t('whatsapp.templateLanguage')}</TableCell>
                <TableCell>{t('whatsapp.templateCategory')}</TableCell>
                <TableCell>{t('whatsapp.templateStatus')}</TableCell>
                {canManageWhatsApp && <TableCell>{tc('actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id}>
                  <TableCell>{tpl.name}</TableCell>
                  <TableCell>{tpl.language}</TableCell>
                  <TableCell>{tpl.category}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <Chip
                        size="small"
                        color={STATUS_COLOR[tpl.status ?? 'draft'] ?? 'default'}
                        label={t(`whatsapp.status.${tpl.status ?? 'draft'}`)}
                      />
                      {tpl.status === 'rejected' && tpl.rejectionReason ? (
                        <Typography variant="caption" color="error">
                          {tpl.rejectionReason}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  {canManageWhatsApp && (
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {tpl.parameterCount > 0 ? (
                          <Button
                            size="small"
                            onClick={() => {
                              if (!hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
                              setMappingTemplate(tpl);
                            }}
                          >
                            {t('whatsapp.editParameterSources')}
                          </Button>
                        ) : null}
                        {tpl.status === 'rejected' ? (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              if (!hasPermission(MANAGE_WHATSAPP_PERMISSION)) return;
                              setDeletingTemplate(tpl);
                            }}
                          >
                            {tc('delete')}
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <WhatsAppTemplateCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <WhatsAppTemplateSourcesDialog
        template={mappingTemplate}
        onClose={() => setMappingTemplate(null)}
      />

      <ConfirmDialog
        open={!!deletingTemplate}
        title={t('whatsapp.deleteTemplateTitle')}
        message={t('whatsapp.deleteTemplateConfirm', { name: deletingTemplate?.name ?? '' })}
        confirmColor="error"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeletingTemplate(null)}
        loading={deleteTemplate.isPending}
      />
    </Stack>
  );
}
